import type { PropertyImage } from '@dreamstack/web/lib/prisma/properties'
import Image from 'next/legacy/image'
import type { FunctionComponent } from 'react'
import tw, { styled } from 'twin.macro'
import { transformPropstackImageUrl } from '..'
import {
  ColoredGradient,
  Css_ImageHeadingPropProject,
  FadeIn,
  GridWithGaps,
  MainContainer,
  TransparentGradientTopToBottom,
} from '../index'

const HeaderContainer = tw.div`w-full relative flex-shrink-0 height[619px] md:height[485px] ml:height[710px]`

const HeaderImageContainer = styled.div(({}: {}) => [
  tw`w-full h-full absolute overflow-hidden`,
  `z-index: -1;`,
])

const TransparentSpaceBlocker = tw.div`w-full flex-shrink-0 bg-transparent -mt-px-96 md:(-mt-px-128) lg:(-mt-px-196)`

const HeaderTextContainer = styled.div<{
  objectHasNoImage: boolean
}>(({ objectHasNoImage }) => [
  tw`h-full whitespace-pre-wrap text-white md:(mt-px-96)`,
  objectHasNoImage && tw`text-accentroGray-full`,
])

const HeaderTextPlacement = tw.div`flex flex-col justify-center h-full break-words overflow-clip col-start-1 col-span-12 lg:(my-px-48)`
//characterCount is needed because the very long titles it overlap with the navbar
const StyledHeaderImageText = styled.h1<{
  smallFont?: boolean
  characterCount: number
}>(({ smallFont, characterCount }) => [
  Css_ImageHeadingPropProject,
  smallFont && tw`text-px-32 leading-px-44`,
  characterCount > 80 &&
    tw`text-px-28 line-height[40px] sm:(text-px-32 leading-px-44) ml:(text-px-72 leading-px-80)`,
])

export const HeaderImage: FunctionComponent<
  React.PropsWithChildren<{
    title: string
    image?: Pick<PropertyImage, 'title' | 'url' | 'id'>
    smallFont?: boolean
    isObject?: boolean
    characterCount?: number
  }>
> = ({ image, title, smallFont, isObject, characterCount }) => {
  const imageUrl = !!image
    ? transformPropstackImageUrl({ url: image.url, size: 'default' })
    : ''
  const objectHasNoImage = !!isObject && !image
  return (
    <div>
      <TransparentSpaceBlocker />
      <HeaderContainer>
        <HeaderImageContainer>
          {!!imageUrl && (
            <>
              <Image
                objectFit="cover"
                alt={image?.title ?? 'property image'}
                src={imageUrl}
                layout="fill"
                priority={true}
              />
              {<TransparentGradientTopToBottom />}
            </>
          )}
          {!image && <ColoredGradient />}
          {!!objectHasNoImage && (
            <div tw="absolute inset-0 bg-accentroAquaVeryLight-full"></div>
          )}
        </HeaderImageContainer>
        <HeaderTextContainer objectHasNoImage={objectHasNoImage}>
          <MainContainer tw="h-full">
            <GridWithGaps tw="h-full">
              <HeaderTextPlacement>
                <FadeIn>
                  <StyledHeaderImageText
                    characterCount={characterCount ?? 0}
                    smallFont={smallFont}
                    dangerouslySetInnerHTML={{ __html: title }}
                  />
                </FadeIn>
              </HeaderTextPlacement>
            </GridWithGaps>
          </MainContainer>
        </HeaderTextContainer>
      </HeaderContainer>
    </div>
  )
}
